import React, { useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import ErrorDialog from './ErrorDialog';
import SuccessDialog from './SuccessDialog';
import {
    notifyAuthExpired,
    isAuthExpiredError,
    SESSION_REFRESH_MESSAGE,
    SESSION_REFRESH_TITLE,
} from '../../../utils/auth-error-utils';

/* Example usage

onErrorConfirm is useful - do some actions when the user clicks OK on the error dialog
override onSuccess to prevent the default success dialog

<ApiIconButton
          apiCall={handleConfirm}          
          onSuccess={() => {}}          
          buttonLabel="Confirm"
          disabled={isConfirmDisabled} // Disable the button based on validation
          errorTitle="Email sending error"
          errorMessage="There was a problem sending the email. Please try again."
          onErrorConfirm={resetAndCancel}
        />

*/

const ApiIconButton = ({
    apiCall,
    onSuccess,
    onError,
    icon,
    buttonStyle,
    errorTitle,
    errorMessage,
    successTitle,
    successMessage,
    disabled = false,
    onErrorConfirm = () => {},
    onSuccessConfirm = () => {},
    showSuccessDialog = true,
    ariaLabel,
}) => {
    const [openErrorDialog, setOpenErrorDialog] = useState(false);
    const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
    const [errorDetails, setErrorDetails] = useState(null);
    const [errorTitleOverride, setErrorTitleOverride] = useState(null);
    const [errorMessageOverride, setErrorMessageOverride] = useState(null);
    const [showReloadAction, setShowReloadAction] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const defaultErrorHandler = (error) => {
        const isAuthExpired = isAuthExpiredError(error);
        if (isAuthExpired) {
            notifyAuthExpired({ source: 'api-icon-button' });
            return;
        }
        setErrorDetails(isAuthExpired ? null : error.toString());
        setErrorTitleOverride(isAuthExpired ? SESSION_REFRESH_TITLE : null);
        setErrorMessageOverride(isAuthExpired ? SESSION_REFRESH_MESSAGE : null);
        setShowReloadAction(isAuthExpired);
        setOpenErrorDialog(true);
    };

    const defaultSuccessHandler = () => {
        setOpenSuccessDialog(true);
    };

    const handleClick = async () => {
        setIsLoading(true);
        try {
            console.warn('Operation started...');
            const result = await apiCall();
            console.warn('Operation completed successfully!');

            if (onSuccess) {
                onSuccess(result);
            }

            if (showSuccessDialog) {
                defaultSuccessHandler();
            }
        } catch (error) {
            const isExpected = Boolean(error?.isExpected);
            if (!isExpected) {
                console.error('Operation failed:', error);
            } else {
                console.info('Operation deferred:', error?.message);
            }

            if (onError) {
                const handled = onError(error);
                if (!handled && !isExpected) {
                    defaultErrorHandler(error);
                }
            } else if (!isExpected) {
                defaultErrorHandler(error);
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Box
                sx={{ ...buttonStyle, display: 'inline-block' }}
                onClick={handleClick}
                disabled={isLoading || disabled}
                aria-label={ariaLabel}
            >
                {isLoading ? <CircularProgress sx={{ marginBottom: '-6px' }} size={24} /> : icon}
            </Box>

            <ErrorDialog
                open={openErrorDialog}
                handleClose={() => {
                    setErrorDetails(null);
                    setErrorTitleOverride(null);
                    setErrorMessageOverride(null);
                    setShowReloadAction(false);
                    setOpenErrorDialog(false);
                    onErrorConfirm();
                }}
                title={errorTitleOverride || errorTitle || 'Error'}
                message={errorMessageOverride || errorMessage || 'An error occurred.'}
                details={errorDetails}
                primaryActionText={showReloadAction ? 'Reload Page' : null}
                onPrimaryAction={showReloadAction ? () => window.location.reload() : null}
            />

            <SuccessDialog
                open={openSuccessDialog}
                handleClose={() => {
                    setOpenSuccessDialog(false);
                    onSuccessConfirm();
                }}
                title={successTitle || 'Success'}
                message={successMessage || 'Your action was successful.'}
            />
        </>
    );
};

export default ApiIconButton;
